<template>
  <div class="page-modules">
    <div class="page-modules__content">
      <div v-if="!isPhone" class="page-admin__table-header">
        <BaseRefreshButton class="page-admin__refresh" :size="40" :pending="pending" @refresh="refreshTenantModules" />
      </div>

      <div class="page-modules__table">
        <TableNormal
          :headers="headers"
          :items="modulesAccess"
          :items-per-page="-1"
          class="elevation-1"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :loading="pending"
          :loading-text="$t('page.admin-platforms.loading')"
          hide-default-footer
        >
          <!-- Phones: the table draws the count line above its rows (docs/page-layout.md). -->
          <template #phone-count>
            <span class="number-monospace">{{ modulesAccess.length }}</span>
            {{ ' ' + $t('menu.admin-modules') }}
          </template>
          <template #phone-actions>
            <BaseRefreshButton :pending="pending" @refresh="refreshTenantModules" />
          </template>
          <template v-slot:progress>
            <BaseLoadingBar v-if="pending" />
          </template>
          <template v-slot:item.id="{ item }">
            <div class="page-modules__platform">
              <span class="page-modules__platform-id">
                {{ getModuleName(item) }}
              </span>
              <div v-if="item.tags && item.tags.length" class="page-modules__platform-tags">
                <BaseTag
                  v-for="(tag, index) in item.tags"
                  :key="`${tag}-${index}`"
                  :tone="getModuleTagTone(tag)"
                  preset="soft"
                  :icon="getModuleTagIcon(tag)"
                >
                  {{ $t('tags.' + tag) }}
                </BaseTag>
              </div>
            </div>
          </template>

          <template v-slot:item.hasAccess="{ item }">
            <BaseToggle :value="item.hasAccess" @change="handleToggle(item, ...arguments)" />
          </template>
        </TableNormal>
      </div>
    </div>
    <ConfirmationDialog ref="confirmationDialog" :buttonText="$t('actions.confirm')" @execute="executeHandleToggle">
      <template slot="content">
        <div class="confirmation-dialog__content">
          <i18n path="access-management.set-tenant-module-confirmation">
            <template v-slot:module>
              <span class="confirmation-dialog__action">{{ module ? getModuleName(module) : '' }}</span>
            </template>
          </i18n>
        </div>
      </template>
    </ConfirmationDialog>
  </div>
</template>

<script>
import BaseLoadingBar from '@/components/base/BaseLoadingBar';
import BaseRefreshButton from '@/components/base/BaseRefreshButton.vue';
import BaseToggle from '@/components/base/BaseToggle';
import BaseTag from '@/components/base/BaseTag.vue';
import ConfirmationDialog from '@/components/page-admin-invites/ConfirmationDialog';
import TableNormal from '@/components/table/TableNormal.vue';
import { mapActions, mapGetters } from 'vuex';
import { LOCALE } from '@/util/i18n/locale';
import { getModuleTagTone, getModuleTagIcon } from '@/util/tag-tones/module-tags';
import { viewport } from '@/util/viewport';

export default {
  name: 'PageAdminModules',
  components: {
    TableNormal,
    BaseLoadingBar,
    BaseRefreshButton,
    BaseToggle,
    BaseTag,
    ConfirmationDialog,
  },
  data() {
    return {
      sortBy: ['id'],
      sortDesc: [false],
      headers: [
        { text: this.$t('menu.admin-modules'), value: 'id' },
        { text: this.$t('page.admin-platforms.tenant-access'), value: 'hasAccess', phone: 'actions' },
      ],
      module: null,
      value: null,
    };
  },
  computed: {
    isPhone() {
      return viewport.size === 'phone';
    },
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('tenantModules', ['modulesAccess', 'pending']),
  },
  methods: {
    ...mapActions('tenantModules', ['getTenantModules', 'setTenantModule']),
    async handleToggle(module, value) {
      this.module = module;
      this.value = value;

      this.$refs.confirmationDialog.openDialog();
    },
    async executeHandleToggle() {
      this.setTenantModule({
        moduleId: this.module.id,
        tenantId: this.currentUser.tenantId,
        hasAccess: this.value,
      })
        .then(() => {
          this.$toast.success(this.$t('toast.success'));
        })
        .catch((error) => this.$toast.error(error));
    },
    refreshTenantModules() {
      this.getTenantModules();
    },
    getModuleName(module) {
      return this.$i18n.locale === LOCALE.FR_CA ? module.nameFr : module.nameEn;
    },
    getModuleTagTone,
    getModuleTagIcon,
  },
};
</script>

<style lang="scss">
.page-modules {
  &__content {
    display: flex;
    flex: auto;
    flex-direction: column;
    padding: 32px;
  }

  &__table {
    .v-data-table.theme--light tbody {
      cursor: default;
    }

    .v-data-table.theme--light tbody td {
      color: $color-normal;
    }
  }

  &__platform {
    display: flex;
    align-items: center;
  }

  &__platform-icon {
    padding-right: 8px;
  }

  &__platform-id {
    font-size: 16px;
    padding-right: 12px;
  }

  &__platform-tags {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
  }
}

.v-data-table__progress {
  .base-loading-bar {
    position: absolute;
    top: 73px;
  }
}
</style>
